/**
 * storyText — pure helpers for rendering author-written story bodies that
 * may contain links and YouTube URLs pasted inline.
 *
 * Design constraint (see StoryBody.jsx): stories are stored as PLAIN TEXT.
 * We never store or render author-supplied HTML/iframes. These helpers only
 * *recognise* URLs in that plain text so the render layer can build links and
 * embeds from our own templates — keeping the XSS surface at zero. A URL is
 * turned into an embed only when its host is on the explicit allowlist below.
 */

// Hosts we will turn into an embedded player. Anything not here renders as a
// plain link, never an iframe.
const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtu.be',
  'www.youtu.be',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);

// YouTube video ids are exactly 11 chars of [A-Za-z0-9_-]. Validating the
// shape means a malformed/hostile path segment can never reach the iframe src.
const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;

/**
 * Parse a YouTube start-time param ("90", "90s", "1m30s", "1h2m3s") into
 * whole seconds. Returns 0 when absent/unparseable.
 */
function parseStartSeconds(raw) {
  if (!raw) return 0;
  if (/^\d+$/.test(raw)) return parseInt(raw, 10);
  const m = raw.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
  if (!m) return 0;
  const [, h, min, s] = m;
  return (parseInt(h || 0, 10) * 3600) + (parseInt(min || 0, 10) * 60) + parseInt(s || 0, 10);
}

/**
 * If `raw` is a YouTube URL we recognise, return { id, start } (start in
 * seconds). Otherwise null. Only http(s) + allowlisted hosts are accepted.
 */
export function parseYouTube(raw) {
  let url;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  if (!YOUTUBE_HOSTS.has(url.hostname.toLowerCase())) return null;

  const host = url.hostname.toLowerCase();
  let id = null;
  if (host === 'youtu.be' || host === 'www.youtu.be') {
    id = url.pathname.slice(1).split('/')[0];
  } else if (url.pathname === '/watch') {
    id = url.searchParams.get('v');
  } else {
    // /embed/ID, /shorts/ID, /live/ID, /v/ID
    const m = url.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/?#]+)/);
    if (m) id = m[1];
  }

  if (!id || !VIDEO_ID_RE.test(id)) return null;
  return { id, start: parseStartSeconds(url.searchParams.get('t') || url.searchParams.get('start')) };
}

/** Privacy-friendly embed src for a parsed YouTube reference. */
export function youTubeEmbedSrc({ id, start }) {
  const base = `https://www.youtube-nocookie.com/embed/${id}`;
  return start > 0 ? `${base}?start=${start}` : base;
}

/** True only for http(s) URLs — blocks javascript:, data:, etc. */
export function isSafeHttpUrl(raw) {
  try {
    const { protocol } = new URL(raw);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

// Inline URL matcher. Matches an http(s) URL and deliberately drops trailing
// sentence punctuation so "see https://x.com." links to the URL, not the dot.
const INLINE_URL_RE = /https?:\/\/[^\s<>()]+[^\s<>().,!?:;'"”’]/g;

/**
 * Split one line of text into ordered tokens for inline rendering:
 *   { type: 'text', value } | { type: 'link', value }
 * Only http(s) URLs become links; everything else stays text (React escapes it).
 */
export function tokenizeInline(line) {
  const tokens = [];
  let last = 0;
  // Fresh regex state per call — global regexes are stateful.
  const re = new RegExp(INLINE_URL_RE.source, 'g');
  let m;
  while ((m = re.exec(line)) !== null) {
    const url = m[0];
    if (m.index > last) tokens.push({ type: 'text', value: line.slice(last, m.index) });
    tokens.push(isSafeHttpUrl(url) ? { type: 'link', value: url } : { type: 'text', value: url });
    last = m.index + url.length;
  }
  if (last < line.length) tokens.push({ type: 'text', value: line.slice(last) });
  return tokens;
}

/**
 * Parse a full story body into ordered blocks for StoryBody to render:
 *   { type: 'embed', id, start } | { type: 'text', value }
 *
 * A recognised YouTube URL is pulled OUT of the surrounding prose wherever it
 * appears (inline or on its own line) and rendered as an embed block — an
 * iframe can't sit mid-sentence, so it becomes its own block with the text
 * before/after it kept as adjacent text blocks. Non-YouTube URLs stay in the
 * text and are linkified inline (see tokenizeInline). Newlines are preserved.
 */
export function parseStoryBlocks(text) {
  if (!text) return [];
  const blocks = [];
  let buffer = '';

  const flush = () => {
    // Trim only the block edges (whitespace/newlines adjacent to an embed or
    // the body ends); internal paragraph breaks are preserved.
    const value = buffer.trim();
    if (value.length) blocks.push({ type: 'text', value });
    buffer = '';
  };

  const re = new RegExp(INLINE_URL_RE.source, 'g');
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    const yt = parseYouTube(m[0]);
    if (!yt) continue; // non-YouTube URL → leave inline, linkified in the text block
    buffer += text.slice(last, m.index);
    flush();
    blocks.push({ type: 'embed', ...yt });
    last = m.index + m[0].length;
  }
  buffer += text.slice(last);
  flush();
  return blocks;
}
